import {
  Control,
  FormState,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { PolicyAction, PolicyFormValues } from './policy-dialog';

import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../utils/roles/client-roles';

import PolicyAddressAccordion from './profile-information-accordion/policy-address-accordion';
import PolicyEmailsAccordion from './profile-information-accordion/policy-emails-accordion';
import PolicyGenderAccordion from './profile-information-accordion/policy-gender-accordion';
import PolicyIdsAccordion from './profile-information-accordion/policy-ids-accordion';
import PolicyImsAccordion from './profile-information-accordion/policy-ims-accordion';
import PolicyNotesAccordion from './profile-information-accordion/policy-notes-accordion';
import PolicyOrganizationsAccordion from './profile-information-accordion/policy-organizations-accordion';
import PolicyPhonesAccordion from './profile-information-accordion/policy-phones-accordion';
import PolicyRelationsAccordion from './profile-information-accordion/policy-relations-accordion';
import PolicyWebsitesAccordion from './profile-information-accordion/policy-websites-accordion';
import PolicyEmployeeIdTextField from './profile-information-accordion/policy-employee-id-textfield';
import PolicyManagerEmailTextField from './profile-information-accordion/policy-manager-email-textfield';
import PolicyWorkPhoneTextField from './profile-information-accordion/policy-work-phone-textfield';
import PolicyPrimaryOrganizationSection from './profile-information-accordion/policy-primary-organization-section';
import { InformationSchema } from '../user-management/add-user-dialog/user-schema';
import z from 'zod';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

interface SetProfileInfoStepProps {
  control: Control<PolicyFormValues>;
  watch: UseFormWatch<PolicyFormValues>;
  formState: FormState<PolicyFormValues>;
  getValues: UseFormGetValues<PolicyFormValues>;
  setValue: UseFormSetValue<PolicyFormValues>;
  directoryRoles: string[];
}

export const SetProfileInfoActionSchema = z.object({
  type: z.literal('Set_Profile_Information'),
  userModelTemplate: InformationSchema.optional(),
});

export type SetProfileInfoActionType = z.infer<
  typeof SetProfileInfoActionSchema
>;

// Type guard to check if an action is SetProfileInfoActionType
function isSetProfileInfoAction(
  action: PolicyAction
): action is SetProfileInfoActionType {
  return action.type === 'Set_Profile_Information';
}

export default function SetProfileInfoStep({
  control,
  watch,
  formState,
  getValues,
  setValue,
  directoryRoles,
}: SetProfileInfoStepProps) {
  const actionData = watch('actionData');
  const idx = actionData
    .map((action) => action.type)
    .indexOf('Set_Profile_Information');
  const actionAtIndex = actionData[idx];
  if (!isSetProfileInfoAction(actionAtIndex)) {
    return null;
  }

  return (
    <Box minWidth={800}>
      <Box>
        <Typography sx={{ fontSize: 14, fontWeight: 'bold', mb: 1, ml: 17 }}>
          Employee Related
        </Typography>
        <Stack
          flexDirection="row"
          flexWrap="wrap"
          gap={2}
          justifyContent="center"
        >
          <PolicyEmployeeIdTextField
            watch={watch}
            setValue={setValue}
            disabled={
              !isAbleToSeeUIElement(
                directoryRoles,
                DirectoryRoles.EditExternalIds.value
              )
            }
            fieldName={`actionData.${idx}.userModelTemplate.externalIds`}
          />
          <PolicyWorkPhoneTextField
            watch={watch}
            setValue={setValue}
            disabled={
              !isAbleToSeeUIElement(
                directoryRoles,
                DirectoryRoles.EditPhoneNumbers.value
              )
            }
            fieldName={`actionData.${idx}.userModelTemplate.phones`}
          />
          <PolicyManagerEmailTextField
            watch={watch}
            setValue={setValue}
            disabled={
              !isAbleToSeeUIElement(
                directoryRoles,
                DirectoryRoles.EditRelationships.value
              )
            }
            fieldName={`actionData.${idx}.userModelTemplate.relations`}
          />
          <Box sx={{ width: 400 }}></Box>
        </Stack>
        <Divider sx={{ my: 2 }} />
        <Typography sx={{ fontSize: 14, fontWeight: 'bold', mb: 1, ml: 17 }}>
          Primary Organization
        </Typography>
        <Stack
          flexDirection="row"
          flexWrap="wrap"
          gap={2}
          justifyContent="center"
        >
          <PolicyPrimaryOrganizationSection
            watch={watch}
            setValue={setValue}
            disabled={
              !isAbleToSeeUIElement(
                directoryRoles,
                DirectoryRoles.EditOrganizations.value
              )
            }
            fieldName={`actionData.${idx}.userModelTemplate.organizations`}
          />
          <Box sx={{ width: 400 }}></Box>
        </Stack>
      </Box>
      <Box sx={{ p: 2 }}>
        <PolicyAddressAccordion
          control={control}
          errors={formState.errors}
          setValue={setValue}
          watch={watch}
          getValues={getValues}
          disabled={
            !isAbleToSeeUIElement(
              directoryRoles,
              DirectoryRoles.EditAddresses.value
            )
          }
          fieldName={`actionData.${idx}.userModelTemplate.addresses`}
        />
        <PolicyEmailsAccordion
          control={control}
          errors={formState.errors}
          watch={watch}
          disabled={
            !isAbleToSeeUIElement(
              directoryRoles,
              DirectoryRoles.EditEmailAddress.value
            )
          }
          fieldName={`actionData.${idx}.userModelTemplate.emails`}
        />
        <PolicyGenderAccordion
          control={control}
          errors={formState.errors}
          watch={watch}
          user={null}
          disabled={
            !isAbleToSeeUIElement(
              directoryRoles,
              DirectoryRoles.EditGender.value
            )
          }
          fieldName={`actionData.${idx}.userModelTemplate.gender`}
        />
        <PolicyIdsAccordion
          control={control}
          watch={watch}
          errors={formState.errors}
          disabled={
            !isAbleToSeeUIElement(
              directoryRoles,
              DirectoryRoles.EditExternalIds.value
            )
          }
          fieldName={`actionData.${idx}.userModelTemplate.externalIds`}
        />
        <PolicyImsAccordion
          control={control}
          watch={watch}
          errors={formState.errors}
          disabled={
            !isAbleToSeeUIElement(
              directoryRoles,
              DirectoryRoles.EditInstantMessaging.value
            )
          }
          fieldName={`actionData.${idx}.userModelTemplate.ims`}
        />
        <PolicyNotesAccordion
          control={control}
          user={null}
          disabled={
            !isAbleToSeeUIElement(
              directoryRoles,
              DirectoryRoles.EditNotes.value
            )
          }
          fieldName={`actionData.${idx}.userModelTemplate.notes`}
        />

        <PolicyOrganizationsAccordion
          control={control}
          watch={watch}
          errors={formState.errors}
          setValue={setValue}
          getValues={getValues}
          disabled={
            !isAbleToSeeUIElement(
              directoryRoles,
              DirectoryRoles.EditOrganizations.value
            )
          }
          fieldName={`actionData.${idx}.userModelTemplate.organizations`}
        />
        <PolicyPhonesAccordion
          control={control}
          watch={watch}
          errors={formState.errors}
          disabled={
            !isAbleToSeeUIElement(
              directoryRoles,
              DirectoryRoles.EditPhoneNumbers.value
            )
          }
          fieldName={`actionData.${idx}.userModelTemplate.phones`}
        />
        <PolicyRelationsAccordion
          control={control}
          watch={watch}
          errors={formState.errors}
          disabled={
            !isAbleToSeeUIElement(
              directoryRoles,
              DirectoryRoles.EditRelationships.value
            )
          }
          fieldName={`actionData.${idx}.userModelTemplate.relations`}
        />
        <PolicyWebsitesAccordion
          control={control}
          errors={formState.errors}
          watch={watch}
          disabled={
            !isAbleToSeeUIElement(
              directoryRoles,
              DirectoryRoles.EditWebsites.value
            )
          }
          fieldName={`actionData.${idx}.userModelTemplate.websites`}
        />
      </Box>
    </Box>
  );
}
